<template>
	<div class="chat-overlay" v-if="isOverlayVisible" :class="{ 'desktop-side-panel': !isMobile, closing: isClosing }">
		<div class="overlay-content" :class="{ 'desktop-content': !isMobile, closing: isClosing }">
			<!-- Top Toolbar -->
			<div class="top-toolbar" :class="{ 'desktop-content': !isMobile }">
				<!-- Desktop: New chat button first, then close button -->
				<template v-if="isMobile">
					<button class="back-button" @click="closeOverlay">
						<InlineSvg src="/images/stanley/chevron-left-large.svg" />
					</button>

					<!-- Chat Selector -->
					<ChatSelector :chats="availableChats" :currentChatId="activeChatId" @chat-selected="switchToChat" />

					<button class="create-new-button" @click="startNewChat">
						<InlineSvg src="/images/stanley/write-dark.svg" />
					</button>
				</template>

				<!-- Mobile: Original layout -->
				<template v-else>
					<button class="create-new-button" @click="startNewChat">
						<InlineSvg src="/images/stanley/write-dark.svg" />
					</button>

					<!-- Chat Selector -->
					<ChatSelector :chats="availableChats" :currentChatId="activeChatId" @chat-selected="switchToChat" />

					<button class="close-button" @click="closeOverlay">
						<InlineSvg src="/images/icons-2025/x-close.svg" />
					</button>
				</template>
			</div>

			<!-- Dynamic Dashboard Checklist -->
			<div class="checklist-wrapper" :class="{ 'checklist-wrapper-minimized': checklistMinimized }">
				<DynamicDashboardChecklist :minimized="checklistMinimized" @minimized-clicked="maximizeChecklist" />
			</div>

			<!-- Main Content Area -->
			<div class="main-content">
				<!-- Chat Container -->
				<div class="chat-container" :class="{ hidden: !isChatTabActive }">
					<div class="messages-container" ref="messagesContainer" @scroll="handleScroll">
						<div v-for="message in chatMessages" :key="message.id" class="message" :class="message.type">
							<!-- Show Stanley icon for AI messages -->
							<div v-if="message.type === messageTypes.RECEIVED" class="ai-message-header">
								<div class="stanley-icon">
									<InlineSvg src="/images/dynamic_onboarding_v2/onboarding-stanley.svg" />
								</div>
								<div
									v-if="message.id === currentMessage?.id && (isAIResponding || isAIStreaming) && !message.content"
									class="thinking-indicator"
								>
									<div class="thinking-text">
										<span class="gradient-text">{{ currentStatus }}</span>
									</div>
								</div>
							</div>

							<!-- Chat Message Content for products -->
							<ChatMessageContent
								:message="message"
								@view-product="handleViewProduct"
								@view-image="handleViewImage"
								@preview-attachment-image="handlePreviewAttachmentImage"
							/>

							<!-- Message Action Menu for AI messages -->
							<MessageActionMenu
								v-if="message.type === messageTypes.RECEIVED && message.content"
								:message="message"
								:feedback-status="message.feedback_status || 0"
								:is-disabled="isAIResponding || isAIStreaming"
								:show-checkmark="copiedMessageId === message.id"
								@thumbs-up="handleThumbsUp"
								@thumbs-down="handleThumbsDown"
								@refresh="handleRefresh"
								@copy="handleCopy"
							/>
						</div>
					</div>

					<!-- Action Buttons Container - Only show when no messages -->
					<div v-if="chatMessages.length === 0" class="action-buttons-container">
						<div class="action-buttons-title">
							<div class="action-buttons-icon">
								<InlineSvg src="/images/dynamic_onboarding_v2/onboarding-stanley.svg" />
								<div class="spacer" style="height: 4px;"></div>
							</div>
							<TypingTitle title="How can I help?" />
						</div>
						<div class="action-buttons-scroll-container">
							<div class="action-buttons-wrapper">
								<ActionButton text="Create a Product" icon-src="images/icons-2025/ai-generate-product.svg" @click="createProduct" />
								<ActionButton
									text="Create a Marketing Post"
									icon-src="images/icons-2025/ai-document.svg"
									@click="createMarketingPost"
								/>
							</div>
						</div>
					</div>
				</div>

				<!-- Preview Container -->
				<div class="preview-container" :class="{ hidden: !isPreviewTabActive }">
					<div class="preview-content">
						<div class="preview-placeholder">
							<svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
								<path
									d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z"
									stroke="currentColor"
									stroke-width="2"
									stroke-linecap="round"
									stroke-linejoin="round"
								/>
								<polyline
									points="14,2 14,8 20,8"
									stroke="currentColor"
									stroke-width="2"
									stroke-linecap="round"
									stroke-linejoin="round"
								/>
							</svg>
							<p>Preview content will appear here</p>
						</div>
					</div>
				</div>
			</div>

			<!-- Bottom Input Area -->
			<div class="bottom-input-area" :class="{ 'desktop-content': !isMobile }">
				<!-- File Attachments Preview -->
				<FileAttachmentPreview :attachments="attachments" @remove-attachment="removeAttachment" @preview-image="previewAttachmentImage" />

				<!-- Expandable Input -->
				<div class="input-container">
					<textarea
						ref="messageInput"
						v-model="messageText"
						@input="adjustInputHeight"
						@keydown.enter="handleEnterKey"
						placeholder="Ask Stanley..."
						class="message-input"
						:style="{ height: inputHeight + 'px' }"
					></textarea>

					<!-- Hidden file input -->
					<input ref="fileInput" type="file" multiple :accept="getAcceptedFileTypes()" @change="handleFileSelect" style="display: none;" />
				</div>

				<!-- Bottom Toolbar -->
				<div class="bottom-toolbar">
					<!-- File attachment button -->
					<button
						class="plus-button"
						@click="openFileDialog"
						:disabled="attachments.length >= maxAttachments || isAIResponding || isAIStreaming"
						:title="attachments.length >= maxAttachments ? 'Maximum 10 files allowed' : 'Add files'"
					>
						<InlineSvg src="/images/icons-2025/plus.svg" />
					</button>

					<button v-if="isAIResponding || isAIStreaming" class="round-button primary" @click="handleStopStreaming">
						<InlineSvg src="/images/stanley/stop-white.svg" />
					</button>
					<button v-else-if="messageText.trim() || attachments.length > 0" class="round-button primary" @click="handleSendMessage">
						<InlineSvg src="/images/stanley/up-arrow-white.svg" />
					</button>
					<button v-else class="round-button">
						<InlineSvg src="/images/stanley/up-arrow-purple.svg" />
					</button>
				</div>
			</div>
		</div>

		<!-- Image Viewer Modal -->
		<ChatImageViewer
			v-if="isImageViewerOpen"
			:imageSrc="selectedImageSrc"
			:imageAlt="selectedImageAlt"
			:imageFilename="selectedImageFilename"
			@close="closeImageViewer"
		/>

		<!-- Attachment Image Viewer Modal -->
		<ChatImageViewer
			v-if="isAttachmentImageViewerOpen"
			:imageSrc="selectedAttachmentImageSrc"
			:imageAlt="selectedAttachmentImageAlt"
			:imageFilename="selectedAttachmentImageFilename"
			imageType="attachment"
			@close="closeAttachmentImageViewer"
		/>
	</div>
</template>

<script>
	import CONSTANTS from '~/global_helper/constants.js'
	import { mapGetters, mapActions } from 'vuex'
	import userflow from '~/plugins/userflow.js'
	import DynamicDashboardChecklist from '~/pages/dashboard/components/dynamic_dashboard_v2/components/DynamicDashboardChecklist'
	import ActionButton from '~/components/shared/ActionButton'
	import ChatSelector from './ChatSelector'
	import MessageActionMenu from './MessageActionMenu'
	import ChatMessageContent from './ChatMessageContent'
	import ChatImageViewer from './ChatImageViewer'
	import FileAttachmentPreview from './FileAttachmentPreview'

	export default {
		name: 'ChatOverlay',
		components: {
			ChatSelector,
			MessageActionMenu,
			ChatMessageContent,
			ChatImageViewer,
			FileAttachmentPreview,
			DynamicDashboardChecklist,
			ActionButton,
		},
		data() {
			return {
				messageText: '',
				inputHeight: 40,
				minInputHeight: 40,
				maxInputHeight: 108,
				streamingScrollInterval: null,
				isClosing: false,
				copiedMessageId: null,
				isImageViewerOpen: false,
				selectedImageSrc: '',
				selectedImageAlt: '',
				selectedImageFilename: 'chat-image',
				savedScrollPosition: 0, // Track scroll position when overlay closes
				userScrolling: false, // Track if user is manually scrolling
				lastScrollTime: 0, // Track when user last scrolled
				scrollTimeout: null, // Timeout for clearing scroll flag
				// File attachment related data
				attachments: [],
				maxAttachments: 10,
				maxFileSize: 10 * 1024 * 1024, // 10MB in bytes
				fileInputRef: null,
				isAttachmentImageViewerOpen: false,
				selectedAttachmentImageSrc: '',
				selectedAttachmentImageAlt: '',
				selectedAttachmentImageFilename: 'attachment-image',
				checklistMinimized: true,
				isMobile: this.$commonHelper.isMobileWidthOrLess(),
			}
		},
		computed: {
			...mapGetters('Auth', ['user', 'token']),
			...mapGetters('Chat', [
				'isChatOverlayOpen',
				'getCurrentTab',
				'getMessages',
				'getCurrentChatId',
				'getChats',
				'isLoading',
				'isStreaming',
				'getStatus',
			]),
			messageTypes() {
				return CONSTANTS.CHAT_MESSAGE_TYPES
			},
			isOverlayVisible() {
				return this.isChatOverlayOpen
			},
			isChatTabActive() {
				return this.getCurrentTab === 'chat'
			},
			isPreviewTabActive() {
				return this.getCurrentTab === 'preview'
			},
			chatMessages() {
				return this.getMessages
			},
			activeChatId() {
				return this.getCurrentChatId || this.getChats[0]?.id
			},
			availableChats() {
				return this.getChats
			},
			isAIResponding() {
				return this.isLoading
			},
			isAIStreaming() {
				return this.isStreaming
			},
			currentMessage() {
				// Get the most current message from the chat messages
				if (!this.chatMessages || this.chatMessages.length === 0) {
					return null
				}
				return this.chatMessages[this.chatMessages.length - 1]
			},
			currentStatus() {
				return this.getStatus || 'Thinking...'
			},
		},
		methods: {
			...mapActions('Chat', [
				'closeChatOverlay',
				'createNewChat',
				'openChatOverlay',
				'sendMessage',
				'setCurrentTab',
				'setCurrentChat',
				'abortCurrentRequest',
				'refreshMessage',
				'setGeneratedPageData',
				'handleGetProductIdeas',
				'handleCreateMarketingPost',
			]),
			closeOverlay() {
				if (this.isClosing) return

				this.isClosing = true

				// Track chat close event
				this.$stanAnalytics('chat-v2-overlay-closed', {
					meta: { user_id: this.user?.user_id, username: this.user?.username },
					props: { chat_id: this.activeChatId, platform: this.isMobile ? 'mobile' : 'desktop' },
				})

				// Save current scroll position before closing
				const container = this.$refs.messagesContainer
				if (container) {
					this.savedScrollPosition = container.scrollTop
				}

				// Wait for animation to complete before closing (only on desktop)
				const animationDelay = this.isMobile ? 0 : 150
				setTimeout(() => {
					this.closeChatOverlay()
					this.isClosing = false
				}, animationDelay)
			},

			startNewChat() {
				// Track new chat creation event
				this.$stanAnalytics('chat-v2-new-created', {
					meta: { user_id: this.user?.user_id, username: this.user?.username },
					props: { platform: this.isMobile ? 'mobile' : 'desktop' },
				})

				// Reset scroll position for new chat
				this.savedScrollPosition = 0
				this.createNewChat()
			},
			handleEnterKey(event) {
				// Allow Shift+Enter for new line
				if (!event.shiftKey) {
					event.preventDefault()
					this.handleSendMessage()
					this.minimizeChecklist()
				}
			},
			handleSendMessage() {
				if ((this.messageText.trim() || this.attachments.length > 0) && !this.isAIResponding && !this.isAIStreaming) {
					// Track message sent event
					this.$stanAnalytics('chat-v2-message-sent', {
						meta: { user_id: this.user?.user_id, username: this.user?.username },
						props: {
							chat_id: this.activeChatId,
							message_length: this.messageText.length,
							attachments_count: this.attachments.length,
							platform: this.isMobile ? 'mobile' : 'desktop',
						},
					})

					// Send message with attachments
					this.sendMessage({
						messageContent: this.messageText,
						chatId: this.activeChatId,
						attachments: [...this.attachments], // Create a copy of attachments array
					})
					this.messageText = ''
					this.attachments = [] // Clear attachments after sending
					this.inputHeight = this.minInputHeight
					this.adjustInputHeight()

					// Always scroll to bottom when user sends a message
					setTimeout(() => {
						this.scrollToBottom()
					}, 300)
				}
			},
			switchToChat(chatId) {
				// Track chat selection event
				this.$stanAnalytics('chat-v2-selected', {
					meta: { user_id: this.user?.user_id, username: this.user?.username },
					props: { chat_id: chatId, platform: this.isMobile ? 'mobile' : 'desktop' },
				})

				// Reset scroll position when switching chats
				this.savedScrollPosition = 0
				this.setCurrentChat(chatId)
			},
			adjustInputHeight() {
				const textarea = this.$refs.messageInput
				if (textarea) {
					textarea.style.height = 'auto'
					const scrollHeight = textarea.scrollHeight
					this.inputHeight = Math.min(Math.max(scrollHeight, this.minInputHeight), this.maxInputHeight)
				}
			},
			scrollToBottom() {
				this.$nextTick(() => {
					const container = this.$refs.messagesContainer
					if (container) {
						container.scrollTop = container.scrollHeight
					}
				})
			},
			switchToChatTab() {
				this.setCurrentTab('chat')
			},
			switchToPreviewTab() {
				this.setCurrentTab('preview')
			},
			handleMoreToolsButton() {
				// This method will be implemented later to show more tools
				// eslint-disable-next-line no-console
			},
			handleStopStreaming() {
				this.abortCurrentRequest()
			},
			async handleThumbsUp(message) {
				// Prevent duplicate feedback
				if (message.feedback_status === 1) {
					return
				}

				// Track thumbs up event
				this.$stanAnalytics('chat-v2-thumbs-up', {
					meta: { user_id: this.user?.user_id, username: this.user?.username },
					props: {
						chat_id: this.activeChatId,
						message_id: message.id,
						platform: this.isMobile ? 'mobile' : 'desktop',
					},
				})

				// Update UI immediately for better UX
				message.feedback_status = 1

				try {
					const payload = {
						response_id: parseInt(message.id, 10),
						feedback_status: 1,
					}
					await this.$axios.post('/api/v2/chat-ai/give-feedback', payload, {
						baseURL: this.$configSettings.chatAIApiUrlV2,
					})
				} catch (error) {
					// Revert UI state on error
					message.feedback_status = 0
					console.error('Failed to submit thumbs up feedback:', error)
				}
			},
			async handleThumbsDown(message) {
				// Prevent duplicate feedback
				if (message.feedback_status === -1) {
					return
				}

				// Track thumbs down event
				this.$stanAnalytics('chat-v2-thumbs-down', {
					meta: { user_id: this.user?.user_id, username: this.user?.username },
					props: {
						chat_id: this.activeChatId,
						message_id: message.id,
						platform: this.isMobile ? 'mobile' : 'desktop',
					},
				})

				// Update UI immediately for better UX
				message.feedback_status = -1

				try {
					const payload = {
						response_id: parseInt(message.id, 10),
						feedback_status: -1,
					}
					await this.$axios.post('/api/v2/chat-ai/give-feedback', payload, {
						baseURL: this.$configSettings.chatAIApiUrlV2,
					})
				} catch (error) {
					// Revert UI state on error
					message.feedback_status = 0
					console.error('Failed to submit thumbs down feedback:', error)
				}
			},
			handleRefresh(message) {
				// Track refresh event
				this.$stanAnalytics('chat-v2-refresh', {
					meta: { user_id: this.user?.user_id, username: this.user?.username },
					props: {
						chat_id: this.activeChatId,
						message_id: message.id,
						platform: this.isMobile ? 'mobile' : 'desktop',
					},
				})

				// Find the original question that was asked for this AI response
				const messages = this.chatMessages
				const messageIndex = messages.findIndex(msg => msg.id === message.id)
				if (messageIndex > 0) {
					// Get the user message that preceded this AI response
					const originalQuestion = messages[messageIndex - 1].content
					const originalAttachments = messages[messageIndex - 1].attachments || []

					// Call the store action to refresh the message
					this.refreshMessage({
						messageId: message.id,
						originalQuestion,
						chatId: this.activeChatId,
						attachments: originalAttachments,
					})
				}
			},
			handleCopy(message) {
				// Track copy event
				this.$stanAnalytics('chat-v2-copy', {
					meta: { user_id: this.user?.user_id, username: this.user?.username },
					props: {
						chat_id: this.activeChatId,
						message_id: message.id,
						platform: this.isMobile ? 'mobile' : 'desktop',
					},
				})

				// Copy message content to clipboard
				const content = message.content_html ? message.content_html : message.content
				const tempElement = document.createElement('div')
				tempElement.innerHTML = content
				const plainText = tempElement.textContent || tempElement.innerText || ''

				this.$commonHelper.copyToClipboard(plainText, () => {
					// Set the copied message ID to show checkmark
					this.copiedMessageId = message.id

					// Clear the checkmark after 2000ms
					setTimeout(() => {
						this.copiedMessageId = null
					}, 2000)
				})
			},
			handleViewProduct(product) {
				// Track product view event
				this.$stanAnalytics('chat-v2-product-card-clicked', {
					meta: { user_id: this.user?.user_id, username: this.user?.username },
					props: {
						chat_id: this.activeChatId,
						product_id: product.id,
						product_title: product.product?.title || product.title,
						product_generation_ulid: product.ulid,
						product_type: product.type,
						platform: this.isMobile ? 'mobile' : 'desktop',
					},
				})

				userflow.TrackUserflowEvent('chat-v2-product-card-clicked', {
					chat_id: this.activeChatId,
					product_id: product.id,
					product_title: product.product?.title || product.title,
					product_generation_ulid: product.ulid,
					product_type: product.type,
					platform: this.isMobile ? 'mobile' : 'desktop',
				})

				// Save the product in the store before navigation
				this.setGeneratedPageData(product)

				// Navigate to the product creation page with generated=true parameter
				this.$router.push({
					name: 'linksite_add_new_product',
					params: {
						storeId: 'default',
						slug: product?.type || 'digital-download',
					},
					query: { generated: 'true' },
				})

				this.closeChatOverlay()
			},
			handleViewImage(imageData) {
				// Track image view event
				this.$stanAnalytics('chat-v2-image-viewed', {
					meta: { user_id: this.user?.user_id, username: this.user?.username },
					props: {
						chat_id: this.activeChatId,
						platform: this.isMobile ? 'mobile' : 'desktop',
					},
				})

				// Set the image data for the viewer
				this.selectedImageSrc = imageData.image
				this.selectedImageAlt = imageData.title || 'Chat image'
				this.selectedImageFilename = `chat-image-${Date.now()}.jpg`
				this.isImageViewerOpen = true
			},
			closeImageViewer() {
				this.isImageViewerOpen = false
				this.selectedImageSrc = ''
				this.selectedImageAlt = ''
				this.selectedImageFilename = 'chat-image'
			},
			closeAttachmentImageViewer() {
				this.isAttachmentImageViewerOpen = false
				this.selectedAttachmentImageSrc = ''
				this.selectedAttachmentImageAlt = ''
				this.selectedAttachmentImageFilename = 'attachment-image'
			},
			scrollToBottomIfNearBottom() {
				this.$nextTick(() => {
					const container = this.$refs.messagesContainer
					if (container) {
						// Don't auto-scroll if user is actively scrolling
						if (this.userScrolling) {
							return
						}

						// Check if user is near the bottom (within 100px)
						const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 200

						// Only auto-scroll if user is near the bottom
						if (isNearBottom) {
							container.scrollTop = container.scrollHeight
						}
					}
				})
			},
			startStreamingScrollCheck() {
				// Clear any existing interval
				this.stopStreamingScrollCheck()

				// Set up periodic scrolling during streaming
				this.streamingScrollInterval = setInterval(() => {
					if (this.isAIStreaming) {
						this.scrollToBottomIfNearBottom()
					} else {
						this.stopStreamingScrollCheck()
					}
				}, 300) // Check every 300ms during streaming (less aggressive)
			},
			stopStreamingScrollCheck() {
				if (this.streamingScrollInterval) {
					clearInterval(this.streamingScrollInterval)
					this.streamingScrollInterval = null
				}
			},
			handleScroll() {
				// Save scroll position when user manually scrolls
				const container = this.$refs.messagesContainer
				if (container) {
					this.savedScrollPosition = container.scrollTop

					// Mark that user is actively scrolling
					this.userScrolling = true
					this.lastScrollTime = Date.now()

					// Clear the scrolling flag after a short delay
					clearTimeout(this.scrollTimeout)
					this.scrollTimeout = setTimeout(() => {
						this.userScrolling = false
					}, 150) // Wait 150ms after user stops scrolling
				}
			},
			focusInput() {
				// Focus the message input field
				this.$nextTick(() => {
					const input = this.$refs.messageInput
					if (input) {
						input.focus()
					}
				})
			},
			// File attachment methods
			openFileDialog() {
				this.$refs.fileInput.click()
			},
			getAcceptedFileTypes() {
				// Accept common file types
				return 'image/jpeg,image/png,image/jpg,.pdf,.md,.txt'
			},
			handleFileSelect(event) {
				const files = Array.from(event.target.files)
				this.addFiles(files)
				// Reset the input so the same file can be selected again
				event.target.value = ''
			},
			addFiles(files) {
				for (const file of files) {
					if (this.attachments.length >= this.maxAttachments) {
						this.$stanNotify({
							type: 'warning',
							title: '',
							text: `You can only upload ${this.maxAttachments} files at a time.`,
						})
						break
					}

					if (file.size > this.maxFileSize) {
						this.$stanNotify({
							type: 'warning',
							title: '',
							text: `${file.name} is too large. Each file must be 10MB or smaller.`,
						})
						continue
					}

					// Check if file is already attached
					const isDuplicate = this.attachments.some(
						attachment => attachment.name === file.name && attachment.size === file.size && attachment.lastModified === file.lastModified
					)

					if (isDuplicate) {
						this.$stanNotify({
							type: 'warning',
							title: 'Duplicate file',
							text: `${file.name} is already attached.`,
						})
						continue
					}

					this.attachments.push(file)
				}
			},
			removeAttachment(index) {
				this.attachments.splice(index, 1)
			},
			previewAttachmentImage(attachment) {
				this.selectedAttachmentImageSrc = URL.createObjectURL(attachment)
				this.selectedAttachmentImageAlt = attachment.name
				this.selectedAttachmentImageFilename = attachment.name
				this.isAttachmentImageViewerOpen = true
			},
			handlePreviewAttachmentImage(attachment) {
				this.previewAttachmentImage(attachment)
			},
			createProduct() {
				this.minimizeChecklist()
				this.handleGetProductIdeas(false)
				this.$stanAnalytics('chat-v2-create-a-product-clicked', {
					// User_id and username are optional as non login page should not have user object
					meta: { user_id: this.user?.user_id, username: this.user?.username },
					props: { platform: this.isMobile ? 'mobile' : 'desktop' },
				})
			},
			createMarketingPost() {
				this.minimizeChecklist()
				this.handleCreateMarketingPost(false)
			},
			maximizeChecklist() {
				if (this.checklistMinimized) {
					this.checklistMinimized = false
				}
			},
			minimizeChecklist() {
				this.checklistMinimized = true
			},
			handleResize() {
				this.isMobile = this.$commonHelper.isMobileWidthOrLess()
			},
			checkForUrlParams() {
				// Get URL search parameters
				const urlParams = new URLSearchParams(window.location.search)
				const prompt = urlParams.get('prompt')
				if (prompt) {
					this.openChatOverlay()
					this.messageText = prompt
					this.adjustInputHeight()
					this.handleSendMessage()
				}
			},
		},
		mounted() {
			this.inputHeight = this.minInputHeight

			// Add resize listener to update isMobile on screen resize
			window.addEventListener('resize', this.handleResize)

			// Check for URL search parameters on mount
			this.checkForUrlParams()

			// Restore scroll position when overlay opens
			this.$nextTick(() => {
				const container = this.$refs.messagesContainer
				if (container && this.savedScrollPosition > 0) {
					container.scrollTop = this.savedScrollPosition
				} else {
					// If no saved position, scroll to bottom
					this.scrollToBottom()
				}
			})
		},
		beforeDestroy() {
			this.stopStreamingScrollCheck()
			if (this.scrollTimeout) {
				clearTimeout(this.scrollTimeout)
			}

			// Remove resize listener
			window.removeEventListener('resize', this.handleResize)
		},
		watch: {
			chatMessages() {
				// Always scroll to bottom for new messages, unless user is actively scrolling
				this.$nextTick(() => {
					const container = this.$refs.messagesContainer
					if (container && !this.userScrolling) {
						this.scrollToBottom()
					}
				})
			},
			isAIResponding(newVal, oldVal) {
				// When AI starts responding, start thinking status timer
				if (newVal && !oldVal) {
					// Status is now handled by the store
				} else if (!newVal && oldVal) {
					// When AI stops responding, status is cleared by the store
				}
			},
			isAIStreaming(newVal, oldVal) {
				// When streaming starts, set up periodic scrolling and thinking status
				if (newVal && !oldVal) {
					this.startStreamingScrollCheck()
					// Status is now handled by the store
				} else if (!newVal && oldVal) {
					// When streaming stops, clear the intervals and focus input
					this.stopStreamingScrollCheck()
					// Status is now handled by the store
					this.focusInput()
				}
			},
			isOverlayVisible(newVal, oldVal) {
				if (newVal && !oldVal) {
					// Track chat overlay opened event
					this.$stanAnalytics('chat-v2-overlay-opened', {
						meta: { user_id: this.user?.user_id, username: this.user?.username },
						props: { chat_id: this.activeChatId, platform: this.isMobile ? 'mobile' : 'desktop' },
					})
					this.isClosing = false

					// Restore scroll position when overlay becomes visible
					this.$nextTick(() => {
						const container = this.$refs.messagesContainer
						if (container && this.savedScrollPosition > 0) {
							container.scrollTop = this.savedScrollPosition
						} else {
							// If no saved position, scroll to bottom
							this.scrollToBottom()
						}
					})
				}
			},
		},
	}
</script>

<style lang="scss" scoped>
	.chat-overlay {
		position: fixed;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		z-index: 4999;
		display: flex;
		align-items: center;
		justify-content: center;

		&.desktop-side-panel {
			align-items: stretch;
			justify-content: flex-end;
			animation: fadeIn 0.15s ease-out;
			padding: 24px;
			box-sizing: border-box;
			pointer-events: none; /* Allow clicks to pass through to main content */

			&.closing {
				animation: fadeOut 0.15s ease-in;
			}
		}
	}

	@keyframes fadeIn {
		from {
			opacity: 0;
		}
		to {
			opacity: 1;
		}
	}

	@keyframes slideInRight {
		from {
			transform: translateX(100%);
		}
		to {
			transform: translateX(0);
		}
	}

	@keyframes slideInCenter {
		from {
			transform: scale(0.9);
			opacity: 0;
		}
		to {
			transform: scale(1);
			opacity: 1;
		}
	}

	@keyframes slideOutRight {
		from {
			transform: translateX(0);
		}
		to {
			transform: translateX(100%);
		}
	}

	@keyframes slideOutCenter {
		from {
			transform: scale(1);
			opacity: 1;
		}
		to {
			transform: scale(0.9);
			opacity: 0;
		}
	}

	@keyframes fadeOut {
		from {
			opacity: 1;
		}
		to {
			opacity: 0;
		}
	}

	.overlay-content {
		position: relative;
		width: 90%;
		max-width: 800px;
		height: 90%;
		max-height: 700px;
		background: #f9f9ff;
		border-radius: 0;
		box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
		display: flex;
		flex-direction: column;
		overflow: hidden;
		z-index: 2;

		&.desktop-content {
			width: 400px;
			max-width: 400px;
			height: 100%;
			max-height: none;
			box-shadow: -4px 0 20px rgba(0, 0, 0, 0.1);
			border-radius: 16px;
			border: 1px solid #ced4ff;
			background: #f5f7ff;
			animation: slideInRight 0.15s ease-out;
			pointer-events: auto; /* Re-enable pointer events for the chat content */

			&.closing {
				animation: slideOutRight 0.15s ease-in;
			}
		}
	}

	.top-toolbar {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 12px;
		background: #f9f9ff;

		&.desktop-content {
			background: #f5f7ff;
			padding: 8px;
		}

		.back-button {
			background: none;
			border: none;
			padding: 4px 8px 4px 0;
			border-radius: 8px;
			color: #6b7280;
			cursor: pointer;
			transition: all 0.2s ease;

			&:hover {
				background: #e5e7eb;
				color: #374151;
			}
		}

		.create-new-button {
			background: none;
			border: none;
			padding: 8px;
			border-radius: 8px;
			color: #6b7280;
			cursor: pointer;
			transition: all 0.2s ease;

			&:hover {
				background: #e5e7eb;
				color: #374151;
			}
		}

		.close-button {
			background: none;
			border: none;
			padding: 8px;
			border-radius: 8px;
			color: #6b7280;
			cursor: pointer;
			transition: all 0.2s ease;

			&:hover {
				background: #e5e7eb;
				color: #374151;
			}
		}
	}

	.main-content {
		flex: 1;
		display: flex;
		overflow: hidden;
	}

	.chat-container {
		flex: 1;
		max-width: 100%;
		display: flex;
		flex-direction: column;
		transition: all 0.3s ease;
		padding-bottom: 0;

		&.hidden {
			flex: 0;
			overflow: hidden;
		}
	}

	.messages-container {
		flex: 1;
		max-width: 100%;
		overflow-y: auto;
		overflow-x: hidden;
		padding: 12px 16px 36px 16px;
		display: flex;
		flex-direction: column;
		gap: 24px;
	}

	.message {
		display: flex;
		flex-direction: column;
		width: 100%;

		&.sent {
			align-self: flex-end;
			align-items: flex-end;
			max-width: 70%;

			:deep(.message-text) {
				max-width: 100%;
				background: #ffffff;
				color: #131f60;
				border-radius: 12px;
				border: 1px solid #e9ecff;
				padding: 12px 16px;
				font-family: 'Inter', sans-serif;
				font-size: 16px;
				font-weight: 400;
				line-height: 150%;

				:deep(p) {
					margin-bottom: 16px;
				}
			}
		}

		&.received {
			align-self: flex-start;
			align-items: flex-start;
			max-width: 100%;

			.chat-message-content {
				width: 100%;
			}

			:deep(.message-text) {
				max-width: 100%;
				background: none;
				color: #131f60;
				font-family: 'Inter', sans-serif;
				font-size: 16px;
				font-weight: 400;
				line-height: 150%;
				border-radius: 0;

				:deep(p) {
					margin-bottom: 16px;
				}
			}
		}
	}

	.thinking-indicator {
		display: flex;
		align-items: center;
		gap: 8px;
	}

	.ai-message-header {
		display: flex;
		align-items: start;
		margin-bottom: 16px;
		gap: 8px;
	}

	.stanley-icon {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 24px;
		height: 24px;
		color: #6b7280;
	}

	.thinking-icon {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 24px;
		height: 24px;
		color: #6b7280;
	}

	.thinking-text {
		display: flex;
		align-items: center;
		font-family: 'Inter', sans-serif;
		font-size: 16px;
		font-weight: 400;
		line-height: 150%;
		color: #6b7280;
	}

	.gradient-text {
		background: linear-gradient(104deg, #00106d 4.48%, #b58bff 41.28%, #131f60 78.35%);
		background-size: 200% 100%;
		-webkit-background-clip: text;
		-webkit-text-fill-color: transparent;
		background-clip: text;
		animation: gradient-shift 3s ease-in-out infinite;
	}

	@keyframes gradient-shift {
		0%,
		100% {
			background-position: 0% 50%;
		}
		50% {
			background-position: 100% 50%;
		}
	}

	.preview-container {
		flex: 1;
		display: flex;
		transition: all 0.3s ease;
		overflow: hidden;

		&.hidden {
			flex: 0;
			overflow: hidden;
		}
	}

	.preview-content {
		height: 100%;
		display: flex;
		align-items: center;
		justify-content: center;
		background: #f9fafb;
	}

	.preview-placeholder {
		text-align: center;
		color: #9ca3af;

		svg {
			margin-bottom: 16px;
		}

		p {
			font-size: 14px;
			margin: 0;
		}
	}

	.bottom-input-area {
		border-top: 1px solid #ced4ff;
		border-top-left-radius: 16px;
		border-top-right-radius: 16px;
		background: white;
		padding: 16px 16px;

		&.desktop-content {
			border: 1px solid #ced4ff;
			border-radius: 16px;
			margin: 0 16px 16px 16px;
		}
	}

	.input-container {
		position: relative;
	}

	.message-input {
		width: 100%;
		border: none;
		border-radius: 8px;
		padding: 8px 2px;
		font-size: 16px;
		font-weight: 400;
		line-height: 150%;
		color: #021026;
		font-family: 'Inter', sans-serif;
		resize: none;
		outline: none;
		transition: border-color 0.2s ease;

		&::placeholder {
			color: #6875a1;
		}

		&:disabled {
			opacity: 0.6;
			cursor: not-allowed;
		}
	}

	.bottom-toolbar {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 12px;
	}

	.round-button {
		background: #ebebff;
		border-radius: 50%;
		width: 40px;
		height: 40px;
		min-width: 40px;
		min-height: 40px;
		display: flex;
		align-items: center;
		justify-content: center;
		cursor: pointer;
		transition: all 0.2s ease;

		&:hover:not(:disabled) {
			background: #f3f4f6;
			border-color: #9ca3af;
			color: #374151;
		}

		&:disabled {
			opacity: 0.6;
			cursor: not-allowed;
		}

		&.primary {
			background: #6355ff;
		}
	}

	.plus-button {
		background: none;
		border: none;
		height: 40px;
		display: flex;
		align-items: center;
		justify-content: center;
		cursor: pointer;
		padding: 0;

		&:disabled {
			opacity: 0.6;
			cursor: not-allowed;
		}

		:deep(svg path) {
			fill: #6355ff;
		}
	}

	.toggle-container {
		display: flex;
		background: #f9f9ff;
		border: 2px solid #f9f9ff;
		border-radius: 50px;
		overflow: hidden;
		position: relative;
		height: 40px;
		flex: 1;
		max-width: 350px;
	}

	.toggle-option {
		background: none;
		border: none;
		padding: 0 16px;
		flex: 1;
		font-family: Inter;
		font-size: 12px;
		font-weight: 600;
		line-height: 110%;
		color: #6b7280;
		cursor: pointer;
		transition: all 0.2s ease;
		min-width: 80px;
		position: relative;
		z-index: 1;
		display: flex;
		align-items: center;
		justify-content: center;

		&:hover {
			background: #f3f4f6;
		}

		&.active {
			background: white;
			color: #6355ff;
			border-radius: 50px;
			position: relative;
			z-index: 2;
			box-shadow: 0px 0px 1px 0px rgba(20, 20, 20, 0.12), 0px 1px 8px 0px rgba(20, 20, 20, 0.08);
		}
	}

	@media (max-width: 768px) {
		.overlay-content {
			width: 100%;
			height: 100%;
			max-height: none;

			&.desktop-content {
				width: 100%;
				max-width: none;
				border-radius: 0;
				box-shadow: none;
				border-left: none;
			}
		}

		.message {
			max-width: 85%;
		}
	}

	.checklist-wrapper {
		margin: 0 16px;
		position: relative;

		&.checklist-wrapper-minimized {
			margin: 0 !important;
		}
	}

	.action-buttons-container {
		display: flex;
		flex-direction: column;
		width: 100%;
		align-items: start;
		padding: 16px 0;

		.action-buttons-title {
			padding: 0 16px;
			margin-bottom: 24px;
		}

		.action-buttons-scroll-container {
			overflow-x: scroll;
			width: 100%;

			&::-webkit-scrollbar {
				display: none;
			}
			-ms-overflow-style: none;
			scrollbar-width: none;
		}

		.action-buttons-wrapper {
			display: flex;
			gap: 12px;
			padding: 0 16px;
			flex-wrap: nowrap;
			justify-content: flex-start;
			min-width: max-content;
		}
	}
</style>
